// src/utils/photoStore.js
// High-performance photo storage using IndexedDB
// Replaces localStorage to handle 3000+ photos without freezing
// 
// HARDENED VERSION:
// - Auto-recovery from version mismatches
// - Connection state management
// - Retry logic for transient failures
// - Graceful degradation

const DB_NAME = 'VivaJourPhotos';
const DB_VERSION = 3; // Bumped to fix version mismatch errors
const PHOTO_STORE = 'photos';
const ALBUM_STORE = 'albums';
const THUMB_STORE = 'thumbnails';

let dbInstance = null;
let dbInitPromise = null;
let dbInitAttempts = 0;
const MAX_INIT_ATTEMPTS = 3;

/**
 * Initialize the database (singleton pattern)
 * Includes recovery logic for version mismatches and other errors
 */
async function openDB() {
  // Return existing connection if valid
  if (dbInstance) {
    // Check if connection is still open
    try {
      // Simple check - if we can start a transaction, connection is good
      dbInstance.transaction(ALBUM_STORE, 'readonly');
      return dbInstance;
    } catch (e) {
      // Connection closed, need to reconnect
      console.warn('[PhotoStore] Connection closed, reconnecting...');
      dbInstance = null;
      dbInitPromise = null;
    }
  }
  
  if (dbInitPromise) return dbInitPromise;
  
  dbInitPromise = new Promise((resolve, reject) => {
    const attemptOpen = (version = DB_VERSION) => {
      console.log(`[PhotoStore] Opening database (version ${version}, attempt ${dbInitAttempts + 1})...`);
      
      const request = indexedDB.open(DB_NAME, version);
      
      request.onerror = (event) => {
        const error = request.error;
        console.error('[PhotoStore] Failed to open database:', error);
        
        // Handle version mismatch - try to open without specifying version
        if (error?.name === 'VersionError' && dbInitAttempts < MAX_INIT_ATTEMPTS) {
          dbInitAttempts++;
          console.warn('[PhotoStore] Version mismatch detected, attempting recovery...');
          
          // Try to open with whatever version exists
          const recoveryRequest = indexedDB.open(DB_NAME);
          
          recoveryRequest.onsuccess = () => {
            const existingVersion = recoveryRequest.result.version;
            recoveryRequest.result.close();
            console.log(`[PhotoStore] Found existing version: ${existingVersion}`);
            
            // Now open with the higher version
            const targetVersion = Math.max(existingVersion, DB_VERSION);
            attemptOpen(targetVersion);
          };
          
          recoveryRequest.onerror = () => {
            dbInitPromise = null;
            reject(new Error('Failed to recover from version mismatch'));
          };
          
          return;
        }
        
        dbInitPromise = null;
        reject(error);
      };
      
      request.onsuccess = () => {
        dbInstance = request.result;
        dbInitAttempts = 0; // Reset attempts on success
        console.log('[PhotoStore] ✅ Database opened successfully (version ' + dbInstance.version + ')');
        
        // Handle connection closing unexpectedly
        dbInstance.onclose = () => {
          console.warn('[PhotoStore] Database connection closed unexpectedly');
          dbInstance = null;
          dbInitPromise = null;
        };
        
        // Handle version change from another tab
        dbInstance.onversionchange = () => {
          console.warn('[PhotoStore] Database version changed in another tab, closing connection');
          dbInstance.close();
          dbInstance = null;
          dbInitPromise = null;
        };
        
        resolve(dbInstance);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        const oldVersion = event.oldVersion;
        console.log(`[PhotoStore] Upgrading database schema from v${oldVersion} to v${db.version}...`);
        
        // Photo store: id → { blob, metadata }
        if (!db.objectStoreNames.contains(PHOTO_STORE)) {
          const photoStore = db.createObjectStore(PHOTO_STORE, { keyPath: 'id' });
          photoStore.createIndex('albumId', 'albumId', { unique: false });
          photoStore.createIndex('storedAt', 'storedAt', { unique: false });
          console.log('[PhotoStore] Created photos store');
        }
        
        // Thumbnail store: id → { thumbBlob } (separate for faster grid loading)
        if (!db.objectStoreNames.contains(THUMB_STORE)) {
          db.createObjectStore(THUMB_STORE, { keyPath: 'id' });
          console.log('[PhotoStore] Created thumbnails store');
        }
        
        // Album store: id → { pages (with photoIds only), metadata }
        if (!db.objectStoreNames.contains(ALBUM_STORE)) {
          const albumStore = db.createObjectStore(ALBUM_STORE, { keyPath: 'id' });
          albumStore.createIndex('savedAt', 'savedAt', { unique: false });
          console.log('[PhotoStore] Created albums store');
        }
      };
      
      request.onblocked = () => {
        console.warn('[PhotoStore] Database upgrade blocked - please close other tabs');
      };
    };
    
    attemptOpen();
  });
  
  return dbInitPromise;
}

/**
 * Wrapper for database operations with automatic reconnection
 */
async function withDB(operation, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const db = await openDB();
      return await operation(db);
    } catch (err) {
      const isRetryable = 
        err.name === 'InvalidStateError' || // Connection closed
        err.name === 'TransactionInactiveError' ||
        err.message?.includes('connection');
      
      if (isRetryable && attempt < retries) {
        console.warn(`[PhotoStore] Retrying operation (attempt ${attempt + 2}/${retries + 1})...`);
        dbInstance = null;
        dbInitPromise = null;
        await new Promise(r => setTimeout(r, 100 * (attempt + 1))); // Backoff
        continue;
      }
      throw err;
    }
  }
}

/**
 * Generate a short, stable hash from a data URL or URI
 * Deterministic: same input always produces same output
 */
export function generatePhotoId(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') {
    return 'ph_' + Math.random().toString(36).slice(2, 10);
  }
  
  // If it's already a short ID, return it
  if (dataUrl.startsWith('ph_') && dataUrl.length < 20) {
    return dataUrl;
  }
  
  // Extract the data portion for hashing
  const data = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
  
  // Fast hash (djb2 variant with sampling for large strings)
  let hash = 5381;
  const step = Math.max(1, Math.floor(data.length / 1000)); // Sample ~1000 chars max
  for (let i = 0; i < data.length; i += step) {
    hash = ((hash << 5) + hash) ^ data.charCodeAt(i);
  }
  
  // Add length to reduce collisions
  hash = ((hash << 5) + hash) ^ data.length;
  
  return 'ph_' + (hash >>> 0).toString(36);
}

/**
 * Convert data URL to Blob for efficient storage
 * Blobs are ~33% smaller than base64 strings
 */
function dataUrlToBlob(dataUrl) {
  if (!dataUrl || !dataUrl.startsWith('data:')) {
    console.warn('[PhotoStore] Invalid data URL for blob conversion');
    return null;
  }
  
  try {
    const parts = dataUrl.split(',');
    const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const binary = atob(parts[1]);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  } catch (err) {
    console.error('[PhotoStore] Failed to convert data URL to blob:', err);
    return null;
  }
}

/**
 * Convert Blob back to data URL (for display)
 */
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    if (!blob) {
      reject(new Error('No blob provided'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Create a thumbnail for fast grid loading
 * Does NOT affect the stored original
 */
async function createThumbnailBlob(dataUrl, maxSize = 200, quality = 0.6) {
  return new Promise((resolve) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob(
          (blob) => resolve(blob),
          'image/jpeg',
          quality
        );
      } catch (err) {
        console.warn('[PhotoStore] Thumbnail creation failed:', err);
        resolve(null);
      }
    };
    
    img.onerror = () => {
      console.warn('[PhotoStore] Failed to load image for thumbnail');
      resolve(null);
    };
    
    img.src = dataUrl;
  });
}

// ============ PHOTO STORAGE API ============

/**
 * Store a single photo
 * @param {string} dataUrl - The photo as a data URL
 * @param {string} albumId - The album this photo belongs to
 * @param {object} metadata - Optional metadata (gps, timestamp, etc.)
 * @returns {Promise<string>} The photo ID
 */
export async function storePhoto(dataUrl, albumId = 'default', metadata = {}) {
  if (!dataUrl || !dataUrl.startsWith('data:')) {
    console.warn('[PhotoStore] Invalid data URL, skipping store');
    return null;
  }
  
  return withDB(async (db) => {
    const id = generatePhotoId(dataUrl);
    
    // Check if already stored
    const existing = await getPhotoRecord(id);
    if (existing) {
      console.log('[PhotoStore] Photo already exists:', id);
      return id;
    }
    
    const blob = dataUrlToBlob(dataUrl);
    if (!blob) return null;
    
    const thumbBlob = await createThumbnailBlob(dataUrl);
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction([PHOTO_STORE, THUMB_STORE], 'readwrite');
      
      // Store full photo
      tx.objectStore(PHOTO_STORE).put({
        id,
        albumId,
        blob,
        metadata: {
          ...metadata,
          originalSize: dataUrl.length,
          blobSize: blob.size
        },
        storedAt: Date.now()
      });
      
      // Store thumbnail separately
      if (thumbBlob) {
        tx.objectStore(THUMB_STORE).put({
          id,
          blob: thumbBlob
        });
      }
      
      tx.oncomplete = () => {
        console.log('[PhotoStore] ✅ Stored photo:', id, `(${(blob.size / 1024).toFixed(1)}KB)`);
        resolve(id);
      };
      
      tx.onerror = () => {
        console.error('[PhotoStore] Failed to store photo:', tx.error);
        reject(tx.error);
      };
    });
  });
}

/**
 * Store multiple photos in batches (MUCH faster for large albums)
 * @param {Array} photos - Array of photo objects with url/src properties
 * @param {string} albumId - Album ID
 * @param {Function} onProgress - Progress callback (completed, total)
 * @returns {Promise<Map>} Map of originalUrl → photoId
 */
export async function storePhotosBatch(photos, albumId = 'default', onProgress = null) {
  return withDB(async (db) => {
    const mapping = new Map(); // originalUrl → photoId
    const BATCH_SIZE = 20; // Process 20 at a time to avoid memory spikes
    
    console.log(`[PhotoStore] Starting batch store of ${photos.length} photos...`);
    const startTime = Date.now();
    
    // Filter to only valid data URLs
    const validPhotos = photos.filter(p => {
      const url = p.url || p.src;
      return url && url.startsWith('data:');
    });
    
    if (validPhotos.length === 0) {
      console.log('[PhotoStore] No valid photos to store');
      return mapping;
    }
    
    // Check which photos are already stored
    const idsToCheck = validPhotos.map(p => generatePhotoId(p.url || p.src));
    const existingIds = await checkExistingPhotos(idsToCheck);
    
    // Filter to only new photos
    const newPhotos = validPhotos.filter(p => {
      const id = generatePhotoId(p.url || p.src);
      if (existingIds.has(id)) {
        mapping.set(p.url || p.src, id);
        return false;
      }
      return true;
    });
    
    console.log(`[PhotoStore] ${existingIds.size} already stored, ${newPhotos.length} new`);
    
    // Process new photos in batches
    for (let i = 0; i < newPhotos.length; i += BATCH_SIZE) {
      const batch = newPhotos.slice(i, i + BATCH_SIZE);
      
      await new Promise((resolve, reject) => {
        const tx = db.transaction([PHOTO_STORE, THUMB_STORE], 'readwrite');
        const photoStore = tx.objectStore(PHOTO_STORE);
        const thumbStore = tx.objectStore(THUMB_STORE);
        
        let pending = batch.length;
        
        batch.forEach(async (photo) => {
          const url = photo.url || photo.src;
          const id = generatePhotoId(url);
          
          try {
            const blob = dataUrlToBlob(url);
            if (!blob) {
              pending--;
              if (pending === 0) resolve();
              return;
            }
            
            // Store full photo
            photoStore.put({
              id,
              albumId,
              blob,
              metadata: {
                gps: photo.gps || photo.coordinates,
                timestamp: photo.timestamp || photo.dateTaken,
                fileName: photo.fileName,
                originalSize: url.length,
                blobSize: blob.size
              },
              storedAt: Date.now()
            });
            
            // Create and store thumbnail (async, but don't wait)
            createThumbnailBlob(url).then(thumbBlob => {
              if (thumbBlob) {
                try {
                  const thumbTx = db.transaction(THUMB_STORE, 'readwrite');
                  thumbTx.objectStore(THUMB_STORE).put({ id, blob: thumbBlob });
                } catch (e) {
                  // Thumbnail storage failed, non-critical
                  console.warn('[PhotoStore] Failed to store thumbnail:', id);
                }
              }
            });
            
            mapping.set(url, id);
          } catch (err) {
            console.warn('[PhotoStore] Failed to process photo:', err);
          }
          
          pending--;
          if (pending === 0) resolve();
        });
        
        tx.onerror = () => reject(tx.error);
        
        // In case batch was empty or all failed
        if (batch.length === 0) resolve();
      });
      
      // Report progress
      const completed = Math.min(i + BATCH_SIZE, newPhotos.length) + existingIds.size;
      onProgress?.(completed, photos.length);
      
      // Yield to main thread to prevent freezing
      await new Promise(r => setTimeout(r, 0));
    }
    
    const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
    console.log(`[PhotoStore] ✅ Batch complete: ${mapping.size} photos in ${elapsed}s`);
    
    return mapping;
  });
}

/**
 * Check which photo IDs already exist
 */
async function checkExistingPhotos(ids) {
  return withDB(async (db) => {
    const existing = new Set();
    
    return new Promise((resolve) => {
      const tx = db.transaction(PHOTO_STORE, 'readonly');
      const store = tx.objectStore(PHOTO_STORE);
      
      let pending = ids.length;
      if (pending === 0) {
        resolve(existing);
        return;
      }
      
      ids.forEach(id => {
        const request = store.getKey(id);
        request.onsuccess = () => {
          if (request.result) existing.add(id);
          pending--;
          if (pending === 0) resolve(existing);
        };
        request.onerror = () => {
          pending--;
          if (pending === 0) resolve(existing);
        };
      });
    });
  });
}

/**
 * Get raw photo record (internal use)
 */
async function getPhotoRecord(id) {
  return withDB(async (db) => {
    return new Promise((resolve) => {
      const tx = db.transaction(PHOTO_STORE, 'readonly');
      const request = tx.objectStore(PHOTO_STORE).get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => resolve(null);
    });
  });
}

/**
 * Get a photo by ID (returns full data URL)
 */
export async function getPhotoById(id) {
  if (!id) return null;
  
  const record = await getPhotoRecord(id);
  if (!record?.blob) return null;
  
  try {
    const dataUrl = await blobToDataUrl(record.blob);
    return {
      id,
      url: dataUrl,
      metadata: record.metadata
    };
  } catch (err) {
    console.error('[PhotoStore] Failed to convert photo:', err);
    return null;
  }
}

/**
 * Get thumbnail by ID (for fast grid views)
 */
export async function getThumbnailById(id) {
  if (!id) return null;
  
  return withDB(async (db) => {
    return new Promise(async (resolve) => {
      const tx = db.transaction(THUMB_STORE, 'readonly');
      const request = tx.objectStore(THUMB_STORE).get(id);
      
      request.onsuccess = async () => {
        if (!request.result?.blob) {
          // Fallback to full photo if no thumbnail
          const full = await getPhotoById(id);
          resolve(full?.url || null);
          return;
        }
        
        try {
          const dataUrl = await blobToDataUrl(request.result.blob);
          resolve(dataUrl);
        } catch {
          resolve(null);
        }
      };
      
      request.onerror = () => resolve(null);
    });
  });
}

/**
 * Get multiple photos by IDs (batched for performance)
 * @returns {Promise<Map<string, {id, url, metadata}>>}
 */
export async function getPhotosByIds(ids) {
  if (!ids || ids.length === 0) return new Map();
  
  return withDB(async (db) => {
    const results = new Map();
    const uniqueIds = [...new Set(ids)];
    
    console.log(`[PhotoStore] Loading ${uniqueIds.length} photos...`);
    const startTime = Date.now();
    
    return new Promise((resolve) => {
      const tx = db.transaction(PHOTO_STORE, 'readonly');
      const store = tx.objectStore(PHOTO_STORE);
      
      let pending = uniqueIds.length;
      
      uniqueIds.forEach(id => {
        const request = store.get(id);
        
        request.onsuccess = async () => {
          if (request.result?.blob) {
            try {
              const dataUrl = await blobToDataUrl(request.result.blob);
              results.set(id, {
                id,
                url: dataUrl,
                metadata: request.result.metadata
              });
            } catch (err) {
              console.warn('[PhotoStore] Failed to load photo:', id, err);
            }
          }
          
          pending--;
          if (pending === 0) {
            const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
            console.log(`[PhotoStore] ✅ Loaded ${results.size}/${uniqueIds.length} photos in ${elapsed}s`);
            resolve(results);
          }
        };
        
        request.onerror = () => {
          pending--;
          if (pending === 0) resolve(results);
        };
      });
    });
  });
}

/**
 * Get multiple thumbnails by IDs
 */
export async function getThumbnailsByIds(ids) {
  if (!ids || ids.length === 0) return new Map();
  
  return withDB(async (db) => {
    const results = new Map();
    const uniqueIds = [...new Set(ids)];
    
    return new Promise((resolve) => {
      const tx = db.transaction(THUMB_STORE, 'readonly');
      const store = tx.objectStore(THUMB_STORE);
      
      let pending = uniqueIds.length;
      
      uniqueIds.forEach(id => {
        const request = store.get(id);
        
        request.onsuccess = async () => {
          if (request.result?.blob) {
            try {
              const dataUrl = await blobToDataUrl(request.result.blob);
              results.set(id, dataUrl);
            } catch {}
          }
          
          pending--;
          if (pending === 0) resolve(results);
        };
        
        request.onerror = () => {
          pending--;
          if (pending === 0) resolve(results);
        };
      });
    });
  });
}

// ============ ALBUM STORAGE API ============

/**
 * Save album (pages with photo IDs only - very lightweight!)
 */
export async function saveAlbum(albumId, albumData) {
  return withDB(async (db) => {
    console.log(`[PhotoStore] Saving album: ${albumId}`);
    
    // Create lightweight pages (photo IDs only, no data URLs)
    const lightPages = (albumData.pages || []).map(page => {
      // Convert photos array to IDs
      const photoIds = (page.photos || []).map(p => {
        if (typeof p === 'string') return p;
        return p.id || generatePhotoId(p.url || p.src);
      });
      
      // Convert items array to IDs
      const itemIds = (page.items || []).map(item => {
        if (typeof item === 'string') return item;
        return {
          ...item,
          id: item.id || generatePhotoId(item.url),
          url: undefined // Remove the actual URL
        };
      });
      
      return {
        ...page,
        photoIds, // New: array of IDs
        photos: undefined, // Remove full photos
        items: itemIds.length > 0 ? itemIds : undefined
      };
    });
    
    const record = {
      id: albumId,
      title: albumData.title || albumData.tripName,
      tripName: albumData.tripName,
      vibes: albumData.vibes,
      layout: albumData.layout,
      pages: lightPages,
      pageCount: lightPages.length,
      savedAt: Date.now(),
      isSaved: albumData.isSaved ?? true
    };
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(ALBUM_STORE, 'readwrite');
      tx.objectStore(ALBUM_STORE).put(record);
      
      tx.oncomplete = () => {
        console.log(`[PhotoStore] ✅ Album saved: ${albumId} (${lightPages.length} pages)`);
        resolve(true);
      };
      
      tx.onerror = () => {
        console.error('[PhotoStore] Failed to save album:', tx.error);
        reject(tx.error);
      };
    });
  });
}

/**
 * Load album metadata (without hydrating photos)
 */
export async function loadAlbum(albumId) {
  return withDB(async (db) => {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(ALBUM_STORE, 'readonly');
      const request = tx.objectStore(ALBUM_STORE).get(albumId);
      
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  });
}

/**
 * Load album with full photo data (for export/share)
 */
export async function loadAlbumWithPhotos(albumId) {
  const album = await loadAlbum(albumId);
  if (!album) return null;
  
  console.log(`[PhotoStore] Hydrating album: ${albumId}`);
  
  // Collect all photo IDs
  const photoIds = new Set();
  (album.pages || []).forEach(page => {
    (page.photoIds || []).forEach(id => photoIds.add(id));
    (page.items || []).forEach(item => {
      if (item.id) photoIds.add(item.id);
    });
  });
  
  // Load all photos
  const photos = await getPhotosByIds(Array.from(photoIds));
  
  // Reconstruct pages with full photo data
  const hydratedPages = (album.pages || []).map(page => ({
    ...page,
    photos: (page.photoIds || []).map(id => {
      const photo = photos.get(id);
      return photo || { id, url: null };
    }),
    items: (page.items || []).map(item => ({
      ...item,
      url: photos.get(item.id)?.url || item.url
    }))
  }));
  
  return {
    ...album,
    pages: hydratedPages
  };
}

/**
 * List all saved albums (metadata only)
 */
export async function listAlbums() {
  return withDB(async (db) => {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(ALBUM_STORE, 'readonly');
      const request = tx.objectStore(ALBUM_STORE).getAll();
      
      request.onsuccess = () => {
        const albums = (request.result || [])
          .map(a => ({
            id: a.id,
            title: a.title || a.tripName,
            tripName: a.tripName,
            savedAt: a.savedAt,
            pageCount: a.pageCount || a.pages?.length || 0,
            isSaved: a.isSaved
          }))
          .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
        
        resolve(albums);
      };
      
      request.onerror = () => reject(request.error);
    });
  });
}

/**
 * Delete album (optionally delete associated photos)
 */
export async function deleteAlbum(albumId, deletePhotos = false) {
  return withDB(async (db) => {
    return new Promise(async (resolve, reject) => {
      if (deletePhotos) {
        // Delete associated photos first
        const tx = db.transaction([ALBUM_STORE, PHOTO_STORE, THUMB_STORE], 'readwrite');
        const photoStore = tx.objectStore(PHOTO_STORE);
        const thumbStore = tx.objectStore(THUMB_STORE);
        const index = photoStore.index('albumId');
        
        const request = index.openCursor(IDBKeyRange.only(albumId));
        request.onsuccess = () => {
          const cursor = request.result;
          if (cursor) {
            const photoId = cursor.value.id;
            photoStore.delete(photoId);
            thumbStore.delete(photoId);
            cursor.continue();
          }
        };
        
        tx.objectStore(ALBUM_STORE).delete(albumId);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => reject(tx.error);
      } else {
        // Just delete album metadata
        const tx = db.transaction(ALBUM_STORE, 'readwrite');
        tx.objectStore(ALBUM_STORE).delete(albumId);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => reject(tx.error);
      }
    });
  });
}

// ============ UTILITY FUNCTIONS ============

/**
 * Get storage statistics
 */
export async function getStorageStats() {
  return withDB(async (db) => {
    return new Promise((resolve) => {
      const stats = { photos: 0, albums: 0, totalSize: 0 };
      
      const tx = db.transaction([PHOTO_STORE, ALBUM_STORE], 'readonly');
      
      tx.objectStore(PHOTO_STORE).count().onsuccess = (e) => {
        stats.photos = e.target.result;
      };
      
      tx.objectStore(ALBUM_STORE).count().onsuccess = (e) => {
        stats.albums = e.target.result;
      };
      
      // Estimate size from photos
      tx.objectStore(PHOTO_STORE).openCursor().onsuccess = (e) => {
        const cursor = e.target.result;
        if (cursor) {
          stats.totalSize += cursor.value.blob?.size || 0;
          cursor.continue();
        }
      };
      
      tx.oncomplete = () => {
        stats.totalSizeMB = (stats.totalSize / (1024 * 1024)).toFixed(1);
        resolve(stats);
      };
    });
  });
}

/**
 * Clear all data (for testing/reset)
 */
export async function clearAllData() {
  return withDB(async (db) => {
    return new Promise((resolve, reject) => {
      const tx = db.transaction([PHOTO_STORE, THUMB_STORE, ALBUM_STORE], 'readwrite');
      tx.objectStore(PHOTO_STORE).clear();
      tx.objectStore(THUMB_STORE).clear();
      tx.objectStore(ALBUM_STORE).clear();
      
      tx.oncomplete = () => {
        console.log('[PhotoStore] ✅ All data cleared');
        resolve(true);
      };
      tx.onerror = () => reject(tx.error);
    });
  });
}

/**
 * Force close database connection (useful for testing/debugging)
 */
export function closeDB() {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
    dbInitPromise = null;
    console.log('[PhotoStore] Database connection closed');
  }
}

/**
 * Check if database is available and working
 */
export async function checkDBHealth() {
  try {
    const db = await openDB();
    // Try a simple read operation
    const tx = db.transaction(ALBUM_STORE, 'readonly');
    await new Promise((resolve, reject) => {
      const request = tx.objectStore(ALBUM_STORE).count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return { healthy: true, version: db.version };
  } catch (err) {
    return { healthy: false, error: err.message };
  }
}

export default {
  generatePhotoId,
  storePhoto,
  storePhotosBatch,
  getPhotoById,
  getThumbnailById,
  getPhotosByIds,
  getThumbnailsByIds,
  saveAlbum,
  loadAlbum,
  loadAlbumWithPhotos,
  listAlbums,
  deleteAlbum,
  getStorageStats,
  clearAllData,
  closeDB,
  checkDBHealth
};